import {NgModule} from '@angular/core';
import {RouterModule, Routes} from '@angular/router';
import {DashboardComponent} from "./dashboard/dashboard.component";
import {NewProjectComponent} from "./project/new-project.component";
import {ProjectComponent} from "./project/project.component";
import {UserComponent} from "./user/user.component";
import {DirectoryComponent} from "./directories/directory/directory.component";
import {LoginComponent} from "./login/login.component";
import {AuthGuard} from "./shared/auth.guard";
import {DashboardEnabledGuard} from "./shared/dashboard-enabled.guard";
import {LogoutComponent} from "./logout/logout.component";
import {UserRole} from "./interfaces/user";
import {NewUserComponent} from "./user/new-user.component";
import {RegisterComponent} from "./register/register.component";
import {SettingsComponent} from "./settings/settings.component";
import {SearchComponent} from "./search/search.component";
import {ClientComponent} from "./clients/client.component";
import {ClientOverviewTabComponent} from "./clients/client-overview-tab/client-overview-tab.component";
import {ClientCvsTabComponent} from "./clients/client-cvs-tab/client-cvs-tab.component";
import {StudioComponent} from "./cv/studio/studio.component";
import {CvGeneratorPageComponent} from "./cv/cv-generator-page/cv-generator-page.component";
import {CvTailorComponent} from "./cv/cv-tailor/cv-tailor.component";
import {NewClientComponent} from "./clients/new-client.component";
import {QuestionDirectoryComponent} from "./directories/question-directory/question-directory.component";
import {CallbackComponent} from "./callback/callback.component";
import {TableObjectComponent} from "./table-object/table-object.component";
import {DirtyDeactivateGuard} from "./shared/dirty-deactivate-guard.service";
import {ArchiveBinComponent} from "./archive-bin/archive-bin.component";
import {TimesheetComponent} from "./timesheet/timesheet.component";
import {KilometresComponent} from "./registration/kilometres/kilometres.component";
import {TripsEnabledGuard} from "./registration/trips-enabled.guard";
import {MarketVacanciesListComponent} from "./market-vacancies/market-vacancies-list.component";
import {MarketVacancyDetailComponent} from "./market-vacancies/market-vacancy-detail.component";
import {OrganizationsTableComponent} from "./platform/organizations-table.component";
import {SkillCurationComponent} from "./directories/skill-curation/skill-curation.component";
import {PlatformGuard} from "./shared/platform.guard";
import {OpportunitiesComponent} from "./crm/opportunities/opportunities.component";
import {OpportunityDetailComponent} from "./crm/opportunity-detail/opportunity-detail.component";
import {SubmissionsComponent} from "./crm/submissions/submissions.component";
import {OrganizationsComponent} from "./crm/organizations/organizations.component";
import {OrganizationDetailComponent} from "./crm/organizations/organization-detail/organization-detail.component";
import {SubmissionDetailComponent} from "./crm/submission-detail/submission-detail.component";
import {InvoiceComponent} from "./invoice/invoice.component";
import {InvoicesComponent} from "./invoices/invoices.component";
import {CanInvoiceGuard} from "./invoices/can-invoice.guard";
import {CanQuoteGuard} from "./crm/can-quote.guard";
import {QuoteComponent} from "./crm/quote/quote.component";
import {QuotesOverviewComponent} from "./crm/quotes-overview/quotes-overview.component";
import {PendingApprovalsComponent} from "./time-approvals/pending-approvals.component";
import {CompanyTabComponent} from "./clients/company-tab/company-tab.component";
import {CompanyTabGuard} from "./clients/company-tab/company-tab.guard";
import {IntakeEnabledGuard} from "./shared/intake-enabled.guard";
import {IntakeListComponent} from "./intake/intake-list/intake-list.component";
import {IntakeMailViewComponent} from "./intake/intake-mail-view/intake-mail-view.component";

const DEFAULT_ROUTE = 'users/me';

export const routes: Routes = [
  {
    path: 'registration/hours/:type',
    component: TimesheetComponent,
    canActivate: [AuthGuard],
    data: {
      roles: [UserRole.ADMIN, UserRole.STAFFER, UserRole.USER]
    }
  },
  {
    path: 'registration/kilometres',
    component: KilometresComponent,
    canActivate: [AuthGuard, TripsEnabledGuard],
    canDeactivate: [(km: KilometresComponent) => km.canLeave()],
    data: {
      roles: [UserRole.ADMIN, UserRole.STAFFER, UserRole.USER]
    }
  },
  // Relative redirects on purpose: an absolute redirectTo drops ?week and every other query param.
  {
    path: 'registration',
    redirectTo: 'registration/hours/calendar',
    pathMatch: 'full'
  },
  {
    path: 'registration/hours',
    redirectTo: 'registration/hours/calendar',
    pathMatch: 'full'
  },
  {
    path: 'timesheet',
    redirectTo: 'registration/hours/calendar',
    pathMatch: 'full'
  },
  {
    path: 'timesheet/calendar',
    redirectTo: 'registration/hours/calendar',
    pathMatch: 'full'
  },
  {
    path: 'timesheet/tracker',
    redirectTo: 'registration/hours/tracker',
    pathMatch: 'full'
  },
  {
    path: 'time-approvals',
    redirectTo: 'time-approvals/pending',
    pathMatch: 'full'
  },
  {
    path: 'time-approvals/:filter',
    component: PendingApprovalsComponent,
    canActivate: [AuthGuard],
    data: {
      roles: [UserRole.ADMIN, UserRole.STAFFER, UserRole.USER]
    }
  },
  {
    path: 'projects',
    redirectTo: 'projects/view/',
    pathMatch: 'full'
  },
  {
    path: 'projects/new',
    component: NewProjectComponent,
    canActivate: [AuthGuard],
    data: {
      roles: [UserRole.ADMIN, UserRole.STAFFER]
    }
  },
  {
    path: 'projects/:id',
    component: ProjectComponent,
    canActivate: [AuthGuard],
    canDeactivate: [DirtyDeactivateGuard],
    data: {
      roles: [UserRole.USER, UserRole.STAFFER, UserRole.ADMIN]
    }
  },
  {
    path: 'dashboard',
    component: DashboardComponent,
    canActivate: [AuthGuard, DashboardEnabledGuard]
  },
  {
    path: 'users',
    redirectTo: 'users/view/',
    pathMatch: 'full'
  },
  {
    path: 'users/new',
    component: NewUserComponent,
    canActivate: [AuthGuard],
    data: {
      roles: [UserRole.ADMIN, UserRole.STAFFER]
    }
  },
  {
    path: 'users/me',
    component: UserComponent,
    canActivate: [AuthGuard],
    canDeactivate: [DirtyDeactivateGuard],
  },
  {
    path: 'users/:id',
    component: UserComponent,
    canActivate: [AuthGuard],
    canDeactivate: [DirtyDeactivateGuard],
    data: {
      roles: [UserRole.ADMIN, UserRole.STAFFER]
    },
  },
  // Legacy aliases: My Company used to host the CVs sub-routes. Forward old
  // bookmarks to the new CV Generator surface so they don't fall through to
  // the wildcard /users/me redirect. Declared before `company`, which now has children.
  { path: 'company/cvs', redirectTo: 'cv-generator', pathMatch: 'full' },
  { path: 'company/cvs/studio', redirectTo: 'cv-generator/studio', pathMatch: 'full' },
  { path: 'company/cv/studio', redirectTo: 'cv-generator/studio', pathMatch: 'full' },
  { path: 'company/cv/templates', redirectTo: 'cv-generator/studio', pathMatch: 'full' },
  {
    path: 'company',
    component: ClientComponent,
    canActivate: [AuthGuard],
    data: {
      roles: [UserRole.ADMIN]
    },
    // Each tab guards its own edits; the Profile tab also asks for the shell's form.
    children: [
      { path: '', redirectTo: 'profile', pathMatch: 'full' },
      { path: 'profile', component: CompanyTabComponent, canDeactivate: [DirtyDeactivateGuard], data: { tab: 'profile' } },
      { path: 'billing', component: CompanyTabComponent, canActivate: [CompanyTabGuard], canDeactivate: [DirtyDeactivateGuard], data: { tab: 'billing' } },
      { path: 'vat', component: CompanyTabComponent, canActivate: [CompanyTabGuard], canDeactivate: [DirtyDeactivateGuard], data: { tab: 'vat' } },
      { path: 'kilometres', component: CompanyTabComponent, canActivate: [CompanyTabGuard], canDeactivate: [DirtyDeactivateGuard], data: { tab: 'kilometres' } },
      { path: 'reimbursement', component: CompanyTabComponent, canActivate: [CompanyTabGuard], canDeactivate: [DirtyDeactivateGuard], data: { tab: 'reimbursement' } },
      { path: 'quotes', component: CompanyTabComponent, canActivate: [CompanyTabGuard], canDeactivate: [DirtyDeactivateGuard], data: { tab: 'quotes' } },
    ],
  },
  {
    path: 'cv-generator',
    component: CvGeneratorPageComponent,
    canActivate: [AuthGuard],
    data: {
      // STAFFER added so a department-scoped staffer can reach the tailored
      // package queue (ApplicationPackagePolicy is the real server-side
      // gate). Template studio stays admin-only via the child's own guard.
      roles: [UserRole.ADMIN, UserRole.STAFFER]
    },
    children: [
      { path: '', component: ClientCvsTabComponent, data: { cvWorkspace: true } },
      {
        path: 'studio',
        component: StudioComponent,
        canActivate: [AuthGuard],
        data: {
          roles: [UserRole.ADMIN]
        },
      },
    ],
  },
  {
    path: 'cv-generator/tailor/new',
    component: CvTailorComponent,
    canActivate: [AuthGuard],
    canDeactivate: [DirtyDeactivateGuard],
    data: {
      // USER (role-1) added for the consultant self-serve pilot: the hub's
      // tile navigation lands here for a consultant's own package too.
      // CvTailorComponent's own canRelease/isLocked gates keep a role-1
      // caller from finalizing/downloading; ApplicationPackagePolicy is the
      // real server-side gate. STAFFER added so a department-scoped staffer
      // can reach the same release surface for their team's packages.
      roles: [UserRole.ADMIN, UserRole.STAFFER, UserRole.USER]
    },
  },
  {
    path: 'cv-generator/tailor/:packageId',
    component: CvTailorComponent,
    canActivate: [AuthGuard],
    canDeactivate: [DirtyDeactivateGuard],
    data: {
      roles: [UserRole.ADMIN, UserRole.STAFFER, UserRole.USER]
    },
  },
  {
    path: 'clients/new',
    component: NewClientComponent,
    canActivate: [AuthGuard],
    data: {
      roles: [UserRole.ADMIN, UserRole.STAFFER]
    }
  },
  {
    path: 'clients/:id',
    component: ClientComponent,
    canActivate: [AuthGuard],
    canDeactivate: [DirtyDeactivateGuard],
    data: {
      roles: [UserRole.ADMIN, UserRole.STAFFER],
    },
    children: [
      { path: '', component: ClientOverviewTabComponent },
      { path: 'cvs', component: ClientCvsTabComponent },
      { path: 'cvs/studio', component: StudioComponent },
      { path: 'cv/studio', redirectTo: 'cvs/studio', pathMatch: 'full' },
      { path: 'cv/templates', redirectTo: 'cvs/studio', pathMatch: 'full' },
    ],
  },
  {
    path: 'clients',
    redirectTo: 'clients/view/',
    pathMatch: 'full'
  },
  {
    path: 'search',
    component: SearchComponent,
    canActivate: [AuthGuard]
  },
  {
    path: 'settings',
    component: SettingsComponent,
    canActivate: [AuthGuard]
  },
  {
    path: 'directory/questions/:action',
    component: QuestionDirectoryComponent,
    canActivate: [AuthGuard],
    data: {
      roles: [UserRole.ADMIN]
    }
  },
  {
    path: 'directory/questions',
    component: QuestionDirectoryComponent,
    canActivate: [AuthGuard],
    data: {
      roles: [UserRole.ADMIN]
    }
  },
  {
    path: 'directory/:name/:action',
    component: DirectoryComponent,
    canActivate: [AuthGuard],
    data: {
      roles: [UserRole.ADMIN]
    }
  },
  {
    path: 'directory/:name',
    component: DirectoryComponent,
    canActivate: [AuthGuard],
    data: {
      roles: [UserRole.ADMIN]
    }
  },
  {
    path: 'logout',
    component: LogoutComponent,
    canActivate: [AuthGuard]
  },
  {
    path: 'login',
    component: LoginComponent
  },
  {
    path: 'register',
    component: RegisterComponent
  },
  {
    path: 'callback',
    component: CallbackComponent
  },
  {
    path: 'bin',
    redirectTo: 'bin/',
    pathMatch: 'full'
  },
  {
    path: 'bin/:type',
    component: ArchiveBinComponent
  },
  // Market (#intake): the overview moved under /market so Intake can sit beside it.
  // The market-vacancies redirects are permanent: mails, bookmarks and shared links carry them.
  {
    path: 'market',
    redirectTo: 'market/overview/active',
    pathMatch: 'full'
  },
  {
    path: 'market/overview',
    redirectTo: 'market/overview/active',
    pathMatch: 'full'
  },
  {
    path: 'market/vacancies/:id',
    component: MarketVacancyDetailComponent,
    canActivate: [AuthGuard],
    data: {
      roles: [UserRole.USER, UserRole.STAFFER, UserRole.ADMIN]
    }
  },
  {
    path: 'market/overview/:status',
    component: MarketVacanciesListComponent,
    canActivate: [AuthGuard],
    data: {
      roles: [UserRole.USER, UserRole.STAFFER, UserRole.ADMIN]
    }
  },
  {
    path: 'market/intake',
    component: IntakeListComponent,
    canActivate: [AuthGuard, IntakeEnabledGuard],
    data: {
      roles: [UserRole.ADMIN, UserRole.STAFFER]
    }
  },
  {
    path: 'market/intake/:mailId',
    component: IntakeMailViewComponent,
    canActivate: [AuthGuard, IntakeEnabledGuard],
    data: {
      roles: [UserRole.ADMIN, UserRole.STAFFER]
    }
  },
  {
    path: 'market-vacancies',
    redirectTo: 'market/overview/active',
    pathMatch: 'full'
  },
  {
    path: 'market-vacancies/view/:id',
    redirectTo: 'market/vacancies/:id'
  },
  {
    path: 'market-vacancies/:status',
    redirectTo: 'market/overview/:status'
  },
  {
    path: 'opportunities/view/:status',
    component: OpportunitiesComponent,
    canActivate: [AuthGuard],
    data: {
      roles: [UserRole.ADMIN, UserRole.STAFFER]
    }
  },
  {
    path: 'opportunities/:id',
    component: OpportunityDetailComponent,
    canActivate: [AuthGuard],
    data: {
      roles: [UserRole.ADMIN, UserRole.STAFFER]
    }
  },
  {
    // Every quote of the company (#1698), same gate as one quote; before quotes/:id so 'view' is not an id.
    path: 'quotes/view/:status',
    component: QuotesOverviewComponent,
    canActivate: [AuthGuard, CanQuoteGuard],
    data: {
      roles: [UserRole.ADMIN, UserRole.STAFFER, UserRole.USER]
    }
  },
  {
    path: 'quotes',
    redirectTo: 'quotes/view/open',
    pathMatch: 'full'
  },
  {
    // Admin or the Quotes grant, with Quotes on for the company (#1697); the server decides.
    path: 'quotes/:id',
    component: QuoteComponent,
    canActivate: [AuthGuard, CanQuoteGuard],
    canDeactivate: [(quote: QuoteComponent) => quote.canLeave()],
    data: {
      roles: [UserRole.ADMIN, UserRole.STAFFER, UserRole.USER]
    }
  },
  {
    path: 'opportunities',
    redirectTo: 'opportunities/view/open',
    pathMatch: 'full'
  },
  {
    // Admin or the Invoicing grant, which any role can hold; the server decides (#1518).
    path: 'invoices/view/:status',
    component: InvoicesComponent,
    canActivate: [AuthGuard, CanInvoiceGuard],
    data: {
      roles: [UserRole.ADMIN, UserRole.STAFFER, UserRole.USER]
    }
  },
  {
    path: 'invoices',
    redirectTo: 'invoices/view/all',
    pathMatch: 'full'
  },
  {
    path: 'invoices/:id',
    component: InvoiceComponent,
    canActivate: [AuthGuard, CanInvoiceGuard],
    canDeactivate: [(invoice: InvoiceComponent) => invoice.canLeave()],
    data: {
      roles: [UserRole.ADMIN, UserRole.STAFFER, UserRole.USER]
    }
  },
  {
    path: 'submissions/:id',
    component: SubmissionDetailComponent,
    canActivate: [AuthGuard],
    data: {
      roles: [UserRole.ADMIN, UserRole.STAFFER, UserRole.USER]
    }
  },
  {
    path: 'submissions',
    component: SubmissionsComponent,
    canActivate: [AuthGuard],
    data: {
      roles: [UserRole.ADMIN, UserRole.STAFFER]
    }
  },
  // Flat routes, matching Opportunities and Submissions. There is no /crm/*
  // namespace in this app; the crm/ folder groups components, not URLs.
  // The :id route must precede the bare one so it is not shadowed.
  {
    path: 'organizations/:id',
    component: OrganizationDetailComponent,
    canActivate: [AuthGuard],
    data: {
      roles: [UserRole.ADMIN, UserRole.STAFFER]
    }
  },
  {
    path: 'organizations',
    component: OrganizationsComponent,
    canActivate: [AuthGuard],
    data: {
      roles: [UserRole.ADMIN, UserRole.STAFFER]
    }
  },
  {
    path: ':table/view/:type',
    component: TableObjectComponent,
    canActivate: [AuthGuard],
    data: {
      roles: [UserRole.USER, UserRole.STAFFER, UserRole.ADMIN]
    }
  },
  {
    path: 'platform/overview',
    component: OrganizationsTableComponent,
    canActivate: [AuthGuard, PlatformGuard]
  },
  {
    path: 'skills/curation',
    component: SkillCurationComponent,
    canActivate: [AuthGuard, PlatformGuard]
  },
  {
    path: '**',
    redirectTo: DEFAULT_ROUTE
  }
];

@NgModule({
  imports: [RouterModule.forRoot(routes)],
  exports: [RouterModule]
})
export class AppRoutingModule {
  public static readonly DEFAULT_ROUTE = DEFAULT_ROUTE;
}
